/**
 * useAdminAvailability Hook
 *
 * Custom hook to check if any admin users are currently online in the backoffice.
 * Provides real-time availability status for chat widget.
 */
import { useState, useEffect, useCallback } from 'react';
import { logger } from "../utils/logger";
// import { logger } from "../utils/logger";
interface AdminAvailability {
    isOnline: boolean;
    onlineCount: number;
    lastChecked: Date | null;
    isLoading: boolean;
    error: string | null;
}
interface UseAdminAvailabilityOptions {
    checkInterval?: number; // milliseconds
    enabled?: boolean;
}
export const useAdminAvailability = (options: UseAdminAvailabilityOptions = {}) => {
    const { checkInterval = 30000, enabled = true } = options; // Check every 30 seconds by default
    const [availability, setAvailability] = useState<AdminAvailability>({
        isOnline: false,
        onlineCount: 0,
        lastChecked: null,
        isLoading: false,
        error: null
    });
    const checkAdminAvailability = useCallback(async () => {
        if (!enabled)
            return;
        setAvailability(prev => {
            // Only update if state actually changed to prevent unnecessary re-renders
            if (prev.isLoading) {
                return prev; // Already loading, don't update
            }
            return { ...prev, isLoading: true, error: null };
        });
        try {
            // Fast timeout - 2 seconds max to prevent hanging
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 2000);
            
            const response = await fetch('/api/admin/availability', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                signal: controller.signal
            });
            
            clearTimeout(timeoutId);
            
            if (!response.ok) {
                throw new Error(`Failed to check admin availability: ${response.status}`);
            }
            const data = await response.json();
            const newIsOnline = data.isOnline || false;
            const newOnlineCount = data.onlineCount || 0;
            
            setAvailability(prev => {
                // Only update state if values actually changed to prevent unnecessary re-renders
                if (prev.isOnline === newIsOnline && prev.onlineCount === newOnlineCount && !prev.isLoading) {
                    return prev; // No change, don't update
                }
                return {
                    isOnline: newIsOnline,
                    onlineCount: newOnlineCount,
                    lastChecked: new Date(),
                    isLoading: false,
                    error: null
                };
            });
        }
        catch (error) {
            logger.error('Error checking admin availability:', error, {});
            // On timeout or error, treat as no admins available - show ticket form
            const isTimeout = error instanceof Error && (error.name === 'AbortError' || error.message.includes('timeout'));
            setAvailability(prev => {
                // Only update if state actually changed
                if (!prev.isOnline && prev.onlineCount === 0 && !prev.isLoading && prev.error === (isTimeout ? 'Availability check timed out' : (error instanceof Error ? error.message : 'Failed to check admin availability'))) {
                    return prev; // Already in error state, don't update
                }
                return {
                    isOnline: false,
                    onlineCount: 0,
                    lastChecked: new Date(),
                    isLoading: false,
                    error: isTimeout ? 'Availability check timed out' : (error instanceof Error ? error.message : 'Failed to check admin availability')
                };
            });
        }
    }, [enabled]);
    // Initial check - only if enabled
    useEffect(() => {
        if (enabled) {
            checkAdminAvailability();
        }
    }, [enabled, checkAdminAvailability]);
    // Set up polling - only when enabled
    useEffect(() => {
        if (!enabled)
            return;
        const interval = setInterval(checkAdminAvailability, checkInterval);
        return () => clearInterval(interval);
    }, [checkAdminAvailability, checkInterval, enabled]);
    return {
        ...availability,
        refetch: checkAdminAvailability
    };
};
export default useAdminAvailability;
